import { ROUTES_ADMIN_API_BASE } from "@/config/apiConfig";
import { apiFetch } from "@/services/apiService";
import {
  fetchRoutesInformationMockJson,
  isRoutesInformationMockEnabled,
} from "@/utils/routesInformationMock";

export type HostSummaryResponse = {
  totalHostsWaiting: number;
  seatsAvailable: number;
  mostPopularRoutes: Array<{
    from: string;
    to: string;
    count: number;
  }>;
  rideCategories: Array<{
    purpose: string;
    count: number;
  }>;
};

export type HostRouteCardDto = {
  date: string;
  time: string;
  purpose?: string | null;
  seatsAvailable?: number;
  rideDateTime?: string;
  price?: number;
  vehicle?: string;
  rating?: number;
};

export type HostRouteDto = {
  from?: string;
  to?: string;
  fromCity?: string;
  toCity?: string;
  count: number;
  routeCount?: number;
  cards?: HostRouteCardDto[];
};

export type HostOtherRouteDto = {
  fromCity: string;
  toCity: string;
  count: number;
  totalSeatsAvailable: number;
};

export type HostWaitingRoutesResponse = {
  routes: HostRouteDto[];
  otherRoutes: HostOtherRouteDto[];
};

export async function getHostSummary(location?: string): Promise<HostSummaryResponse> {
  if (isRoutesInformationMockEnabled()) {
    return fetchRoutesInformationMockJson<HostSummaryResponse>("get-hostsummary.json");
  }
  const params = new URLSearchParams();
  const loc = location?.trim();
  if (loc) params.set("location", loc);
  const qs = params.toString();
  const url = `${ROUTES_ADMIN_API_BASE}/api/RoutesInformation/get-hostsummary${qs ? `?${qs}` : ""}`;

  const response = await apiFetch(url, {
    method: "GET",
    skipAuth: true,
    headers: { accept: "application/json" },
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch host summary (${response.status})`);
  }
  return response.json() as Promise<HostSummaryResponse>;
}

export async function getHostWaitingRoutes(params: {
  fromCity?: string;
  toCity?: string;
  date?: string;
  page?: number;
}): Promise<HostWaitingRoutesResponse> {
  if (isRoutesInformationMockEnabled()) {
    return fetchRoutesInformationMockJson<HostWaitingRoutesResponse>(
      "get-host-waitingroutes.json"
    );
  }
  const query = new URLSearchParams();
  const fromCity = params.fromCity?.trim();
  const toCity = params.toCity?.trim();
  const date = params.date?.trim();

  if (fromCity) query.set("fromCity", fromCity);
  if (toCity) query.set("toCity", toCity);
  if (date) query.set("date", date);
  query.set("page", String(params.page ?? 1));

  const url = `${ROUTES_ADMIN_API_BASE}/api/RoutesInformation/get-host-waitingroutes?${query.toString()}`;
  const response = await apiFetch(url, {
    method: "GET",
    skipAuth: true,
    headers: { accept: "application/json" },
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch host routes (${response.status})`);
  }
  return response.json() as Promise<HostWaitingRoutesResponse>;
}
